import { ChangeDetectorRef }                            from '@angular/core';
import { Subscription }                                  from 'rxjs';
import { CsChatMessage, createCsChatMessage, encodeCodeTag, splitTrailingError } from '@cs/components/chat';
import { PmChatService, PmChatStreamEvent, PmContextUsage, PmChatAuthError, PmDebugContext } from './pm-chat.service';

/**
 * Meta/housekeeping tools that must NOT render as a ```tool chip — they are hidden, silent, or have
 * their own surface (checklist / download chip). Mirrors HiddenChipTools on the server.
 */
const HIDDEN_CHIP_TOOLS = new Set(['FlagTurn', 'UpdateChatTitle', 'UpdateTodoList', 'ExportArtifact']);

/** Optional hooks a host component can supply. */
export interface PmChatConversationHooks {
	/** Live page/filter context sent with every turn (from AgentContextService). */
	context?: () => string;
	/** Called when the server opens a new session (first turn) — e.g. to sync a URL or list. */
	onSessionStarted?: (startUtc: string, title: string, hasGeneratedTitle: boolean) => void;
	/** Called when the assistant refreshes the generated title mid-stream. */
	onTitle?: (startUtc: string, title: string) => void;
	/** 401 from the backend — the CF session expired; the host decides how to prompt. */
	onAuthRequired?: () => void;
}

/**
 * The chat streaming state-machine shared by the AI-Analyst bubble and full page. It owns the
 * transcript (`messages`), the busy flag and the latest context-window breakdown, and drives them
 * from the SSE stream exactly like the standalone chat app's AppComponent — delta coalescing to one
 * view update per animation frame, atomic fence injection, a stall watchdog, and the OnPush
 * "replace only the streaming row" optimisation. Keeping it in one class means the two PM surfaces
 * don't each re-implement (and drift on) the same ~400 lines.
 */
export class PmChatConversation {
	messages: CsChatMessage[] = [];
	busy = false;
	contextUsage?: PmContextUsage;

	/** Full-prompt breakdown for the advanced debug panel; loaded on demand via {@link loadDebugContext}. */
	debugContext?: PmDebugContext;

	/** Fetch (or refresh) the debug context breakdown. Swallows errors — the panel just shows loading. */
	async loadDebugContext(): Promise<void> {
		try {
			this.debugContext = await this.chat.getDebugContext();
		} catch {
			/* gated off / transient — leave debugContext undefined so the panel shows its loading state */
		} finally {
			this.cdr.markForCheck();
		}
	}

	/** Key of the active session, echoed to the backend on every turn. Null = unsaved new chat. */
	sessionStartUtc: string | null = null;

	private streamingId: string | null = null;
	/** Source of the code run currently streaming; written onto its terminal fence at code.end. */
	private terminalCode = '';
	private pendingDelta = '';
	private flushHandle: number | null = null;
	private streamSub: Subscription | null = null;
	private stallHandle: number | null = null;
	private static readonly STALL_MS = 1600;

	private turnModels: string[] = [];
	private turnModelIds: string[] = [];
	private toolStartedAt = 0;

	constructor(
		private readonly chat: PmChatService,
		private readonly cdr: ChangeDetectorRef,
		private readonly hooks: PmChatConversationHooks = {}
	) {}

	/** Send a user turn and stream the reply. */
	send(text: string): void {
		const clean = (text ?? '').trim();
		if (!clean || this.busy) { return; }

		const history = this.messages;
		const user = createCsChatMessage({ role: 'user', content: clean, timestamp: new Date() });
		this.messages = [...history, user];
		this.busy = true;
		this.streamingId = null;
		this.pendingDelta = '';
		this.turnModels = [];
		this.turnModelIds = [];
		this.cdr.markForCheck();

		const context = this.hooks.context?.() ?? '';
		this.streamSub?.unsubscribe();
		this.streamSub = this.chat.stream(clean, history, this.sessionStartUtc, context).subscribe({
			next:     evt => this.onEvent(evt),
			error:    err => this.onError(err),
			complete: () => { this.flushNow(); this.finishStreaming(); }
		});
	}

	/**
	 * Stop generating, keeping whatever already streamed.
	 *
	 * Tells the SERVER to stop as well as dropping the local stream. Aborting the fetch alone would
	 * no longer stop anything: a turn outlives its request by design (so closing the tab still
	 * finishes and saves the answer), so a dropped connection can't mean "stop" — it's exactly what a
	 * tab close looks like. Without the server call the model would run on and the completed answer
	 * would turn up in the transcript, contradicting the "⏹ Stopped by user." the user just saw.
	 */
	stop(): void {
		if (!this.streamSub) { return; }
		if (this.sessionStartUtc) { void this.chat.stopTurn(this.sessionStartUtc); }
		this.streamSub.unsubscribe();
		this.streamSub = null;
		this.pendingDelta += `${this.pendingDelta || this.streamingId ? '\n\n' : ''}⏹ *Stopped by user.*`;
		this.flushNow();
		this.finishStreaming();
	}

	/** Start a fresh, unsaved conversation. */
	reset(): void {
		this.cancelActiveStream();
		this.messages = [];
		this.sessionStartUtc = null;
		this.busy = false;
		this.cdr.markForCheck();
	}

	/** Load a saved session's transcript into the view. */
	loadSession(startUtc: string, onLoaded?: () => void): void {
		this.cancelActiveStream();
		this.busy = false;
		this.sessionStartUtc = startUtc;
		this.messages = [];
		this.cdr.markForCheck();

		this.chat.loadSession(startUtc)
			.then(turns => {
				this.messages = turns.flatMap(t => {
					// A turn that ended in an error carries a trailing ```error fence (see ChatEndpoints).
					// Lift it back out into its own system row so a reopened session looks exactly like the
					// live stream did: question, whatever the assistant managed to say, then the notice.
					const { body, error } = splitTrailingError(t.response);
					const at   = new Date(t.promptUtc);
					const rows = [createCsChatMessage({ role: 'user', content: t.prompt, timestamp: at })];
					// Skip an empty assistant row: a turn that failed before the first token persists a
					// body of nothing, which would render as a blank bubble above the error notice.
					if (body.trim()) {
						rows.push(createCsChatMessage({ role: 'assistant', content: body, timestamp: at }));
					}
					if (error) {
						// Same mapping as the live path, so the wording matches rather than showing the
						// provider's raw text.
						rows.push(createCsChatMessage({ role: 'system', content: this.friendlyError(error), timestamp: at }));
					}
					return rows;
				});
				this.cdr.markForCheck();
				onLoaded?.();
				this.reattach(startUtc, onLoaded);
			})
			.catch(() => this.cdr.markForCheck());
	}

	/**
	 * If a turn for this session is STILL generating server-side, drop the checkpointed partial and
	 * resume the live stream instead.
	 *
	 * A turn outlives its request, so a reload lands here while the server is still writing the answer.
	 * The transcript we just rendered is only the last checkpoint (up to ~1.5s stale) and will never
	 * move on its own; the server replays that turn's events from the start, so the last assistant row
	 * is DROPPED and rebuilt from the replay — otherwise the partial and the replay would double up.
	 * A 204 (nothing running) completes with no events and leaves the transcript exactly as loaded.
	 */
	private reattach(startUtc: string, onLoaded?: () => void): void {
		let opened = false;
		this.streamSub?.unsubscribe();
		this.streamSub = this.chat.attach(startUtc).subscribe({
			next: evt => {
				if (!opened) {
					opened = true;
					// The turn is live: discard its checkpointed row, the replay is authoritative.
					const last = this.messages[this.messages.length - 1];
					if (last && last.role === 'assistant') { this.messages = this.messages.slice(0, -1); }
					this.busy = true;
					this.streamingId = null;
					this.pendingDelta = '';
					this.turnModels = [];
					this.turnModelIds = [];
				}
				this.onEvent(evt);
			},
			error: () => { this.flushNow(); this.finishStreaming(); },
			complete: () => {
				if (!opened) { return; }          // 204 — nothing was in flight
				this.flushNow();
				this.finishStreaming();
				onLoaded?.();
			}
		});
	}

	/** Release timers/subscriptions — call from the host's ngOnDestroy. */
	destroy(): void {
		this.streamSub?.unsubscribe();
		this.cancelFlush();
		this.clearWatchdog();
	}

	// ── stream handling ───────────────────────────────────────────────────────

	private onEvent(evt: PmChatStreamEvent): void {
		this.pokeWatchdog();
		switch (evt.type) {
			case 'session.started':
				if (evt.startUtc) {
					this.sessionStartUtc = evt.startUtc;
					this.hooks.onSessionStarted?.(evt.startUtc, evt.title ?? 'New chat', evt.hasGeneratedTitle ?? true);
				}
				break;
			case 'session.title':
				if (evt.startUtc && evt.title) { this.hooks.onTitle?.(evt.startUtc, evt.title); }
				break;
			case 'message.started':
				this.recordModel(evt.model, evt.provider);
				if (this.streamingId) { this.setActivity({ label: `${this.friendlyModel(evt.model ?? '')} is thinking…` }); }
				break;
			case 'tool.start':
				this.toolStartedAt = performance.now();
				this.setActivity({ label: this.friendlyTool(evt.tool) });
				break;
			case 'tool.end': {
				this.setActivity(null);
				if (!HIDDEN_CHIP_TOOLS.has(evt.tool ?? '')) {
					const ms = evt.durationMs ?? Math.round(performance.now() - this.toolStartedAt);
					const chip: Record<string, unknown> = { tool: evt.tool ?? 'tool', durationMs: ms };
					if (evt.input !== undefined && evt.input !== null) { chip['input'] = evt.input; }
					if (typeof evt.summary === 'string')               { chip['summary'] = evt.summary; }
					if (typeof evt.result === 'string')                { chip['result'] = evt.result; }
					this.pendingDelta += `\n\n\`\`\`tool\n${JSON.stringify(chip)}\n\`\`\`\n\n`;
					this.flushNow();
				}
				break;
			}
			case 'todos.updated':
				this.setTodos(evt.items ?? []);
				break;
			case 'context.window':
				this.contextUsage = {
					contextWindow:   evt.contextWindow   ?? 0,
					usedTokens:      evt.usedTokens      ?? 0,
					cacheReadTokens: evt.cacheReadTokens ?? 0,
					components:      evt.components       ?? []
				};
				this.cdr.markForCheck();
				break;
			case 'artifact.created':
				this.pendingDelta += `\n\n\`\`\`artifact\n${JSON.stringify({
					id: evt.id, name: evt.name ?? 'file', mimeType: evt.mimeType ?? '', sizeBytes: evt.sizeBytes ?? 0
				})}\n\`\`\`\n\n`;
				this.flushNow();
				break;
			case 'code.start':
				this.setActivity({ label: 'Running Python…' });
				// Stash the source; it goes onto the fence's info line at code.end, where the status is
				// also stamped (the fence opens bare because the status isn't known yet).
				this.terminalCode = evt.code ?? '';
				this.pendingDelta += '\n\n```terminal\n';
				this.flushNow();
				break;
			case 'code.output':
				this.pendingDelta += evt.text ?? '';
				this.scheduleFlush();
				break;
			case 'code.end':
				// A failed run's error is often the ONLY account of what happened - a crash usually
				// prints nothing - so fold it in as the block's last output instead of dropping it.
				if (evt.ok === false && evt.error) {
					this.pendingDelta += (this.pendingDelta.endsWith('\n') ? '' : '\n') + evt.error.trim();
				}
				this.flushNow();
				this.setTerminalStatus(evt.ok === false ? 'error' : 'ok', this.terminalCode);
				this.terminalCode = '';
				this.pendingDelta += '\n```\n\n';
				this.setActivity(null);
				this.flushNow();
				break;
			case 'message.done':
				if ((evt.durationMs ?? 0) > 0) {
					this.pendingDelta += `\n\n\`\`\`stats\n${JSON.stringify({
						timeToFirstMs: evt.timeToFirstMs ?? 0,
						durationMs:    evt.durationMs ?? 0,
						outputTokens:  evt.outputTokens ?? 0,
						toolsMs:       evt.toolsMs ?? 0,
						models:        this.turnModelIds.join(', ')
					})}\n\`\`\`\n`;
					this.flushNow();
				}
				this.finishStreaming();
				break;
			case 'text.delta':
				this.pendingDelta += evt.value ?? '';
				this.scheduleFlush();
				break;
			case 'error':
				this.appendError(evt.value ?? 'Unknown error');
				this.scheduleFlush();
				break;
		}
	}

	private onError(err: unknown): void {
		if (err instanceof PmChatAuthError) {
			this.flushNow();
			this.finishStreaming();
			this.dropEmptyAssistantRow();
			this.hooks.onAuthRequired?.();
			return;
		}
		this.appendError(`Connection error: ${(err as { message?: string })?.message ?? err}`);
		this.flushNow();
		this.finishStreaming();
	}

	private cancelActiveStream(): void {
		this.streamSub?.unsubscribe();
		this.streamSub = null;
		this.cancelFlush();
		this.clearWatchdog();
		this.pendingDelta = '';
		this.streamingId = null;
	}

	private appendError(message: string): void {
		this.flushNow();
		this.finishStreaming();
		this.dropEmptyAssistantRow();
		const errorMsg = createCsChatMessage({ role: 'system', content: this.friendlyError(message), timestamp: new Date() });
		this.messages = [...this.messages, errorMsg];
		this.cdr.markForCheck();
	}

	private dropEmptyAssistantRow(): void {
		const last = this.messages[this.messages.length - 1];
		if (last && last.role === 'assistant' && !(last.content ?? '').trim()) {
			this.messages = this.messages.slice(0, -1);
		}
	}

	private friendlyError(raw: string): string {
		const text  = (raw ?? '').trim();
		const lower = text.toLowerCase();
		if (lower.includes('unauthorized') || lower.includes('401') || lower.includes('authentication_error')) {
			return '⚠ Your session has expired — please reload the page to sign in again.';
		}
		if (lower.includes('rate') && lower.includes('limit')) {
			return '⚠ The AI provider is rate-limiting requests. Please wait a moment and try again.';
		}
		if (lower.includes('overloaded') || lower.includes('529')) {
			return '⚠ The AI provider is temporarily overloaded. Please try again shortly.';
		}
		if (lower.startsWith('connection error') || lower.includes('failed to fetch') || lower.includes('networkerror')) {
			return '⚠ Couldn’t reach the server. Check that it’s running and try again.';
		}
		return `⚠ ${text.split('\n')[0].trim() || 'Something went wrong while generating the response.'}`;
	}

	private setActivity(activity: { label: string } | null): void {
		this.ensureStreamingRow();
		const index = this.messages.findIndex(m => m.id === this.streamingId);
		if (index === -1) { return; }
		this.messages = this.replaceAt(this.messages, index, { ...this.messages[index], activity, models: [...this.turnModels] });
		this.cdr.markForCheck();
	}

	private setTodos(items: { text: string; done: boolean }[]): void {
		this.ensureStreamingRow();
		const index = this.messages.findIndex(m => m.id === this.streamingId);
		if (index === -1) { return; }
		const previous = this.messages[index];
		const fence    = '```todos\n' + JSON.stringify({ items }) + '\n```\n\n';
		const body     = previous.content.replace(/```todos[ \t]*\n[\s\S]*?\n```[ \t]*\n*/, '');
		this.messages = this.replaceAt(this.messages, index, { ...previous, content: fence + body, models: [...this.turnModels] });
		this.cdr.markForCheck();
	}

	private ensureStreamingRow(): void {
		if (this.streamingId) { return; }
		const assistant = createCsChatMessage({
			role: 'assistant', content: '', timestamp: new Date(), streaming: true, models: [...this.turnModels]
		});
		this.streamingId = assistant.id;
		this.messages = [...this.messages, assistant];
	}

	private recordModel(model?: string, provider?: string): void {
		if (!model) { return; }
		const friendly = this.friendlyModel(model);
		if (this.turnModels[this.turnModels.length - 1] !== friendly) { this.turnModels.push(friendly); }
		const id = provider ? `${provider}/${model}` : model;
		if (this.turnModelIds[this.turnModelIds.length - 1] !== id) { this.turnModelIds.push(id); }
	}

	private friendlyTool(tool?: string): string {
		switch (tool) {
			case 'ExportArtifact':    return 'Creating download…';
			case 'CompareWindows':    return 'Comparing windows…';
			case 'AggregateFact':     return 'Aggregating…';
			case 'GetDatasetRange':   return 'Checking dataset…';
			case 'EscalateToAnalyst': return 'Analysing…';
			case 'SearchDocs':
			case 'ReadDoc':           return 'Reading docs…';
			case 'UpdateChatTitle':   return 'Updating title…';
			default:                  return `Querying ${tool ?? 'tool'}…`;
		}
	}

	private friendlyModel(model: string): string {
		const m = model.toLowerCase();
		if (m.includes('haiku'))  { return 'Haiku'; }
		if (m.includes('sonnet')) { return 'Sonnet'; }
		if (m.includes('opus'))   { return 'Opus'; }
		return model;
	}

	private pokeWatchdog(): void {
		this.clearWatchdog();
		this.stallHandle = window.setTimeout(() => {
			this.stallHandle = null;
			if (this.busy && this.streamingId && !this.pendingDelta) {
				const cur = this.messages.find(m => m.id === this.streamingId);
				if (cur && cur.streaming && !cur.activity) { this.setActivity({ label: 'Working…' }); }
			}
		}, PmChatConversation.STALL_MS);
	}

	private clearWatchdog(): void {
		if (this.stallHandle !== null) { clearTimeout(this.stallHandle); this.stallHandle = null; }
	}

	private scheduleFlush(): void {
		if (this.flushHandle !== null) { return; }
		this.flushHandle = requestAnimationFrame(() => { this.flushHandle = null; this.flush(); });
	}

	private flushNow(): void {
		this.cancelFlush();
		this.appendChunk(this.pendingDelta);
		this.pendingDelta = '';
	}

	private setTerminalStatus(status: 'ok' | 'error', code = ''): void {
		if (!this.streamingId) { return; }
		const i = this.messages.findIndex(m => m.id === this.streamingId);
		if (i === -1) { return; }
		const content = this.messages[i].content ?? '';
		const marker  = '```terminal\n';
		const at = content.lastIndexOf(marker);
		if (at === -1) { return; }
		// Mirror the server-persisted fence: "<status> [code=<base64>]" (ChatEndpoints), so a live
		// block and a reopened one parse identically. base64 keeps the source one space-free token
		// on the info line; stripUiFences drops it before history goes back to the model.
		const info = status + (code ? ' code=' + encodeCodeTag(code) : '');
		const updated = content.slice(0, at) + '```terminal ' + info + '\n' + content.slice(at + marker.length);
		this.messages = this.replaceAt(this.messages, i, { ...this.messages[i], content: updated });
	}

	private cancelFlush(): void {
		if (this.flushHandle !== null) { cancelAnimationFrame(this.flushHandle); this.flushHandle = null; }
	}

	private flush(): void {
		if (!this.pendingDelta) { return; }
		const take  = Math.max(3, Math.ceil(this.pendingDelta.length / 6));
		const delta = this.pendingDelta.slice(0, take);
		this.pendingDelta = this.pendingDelta.slice(take);
		this.appendChunk(delta);
		if (this.pendingDelta) { this.scheduleFlush(); }
	}

	private appendChunk(delta: string): void {
		if (!delta) { return; }
		const index = this.streamingId ? this.messages.findIndex(m => m.id === this.streamingId) : -1;
		if (index === -1) {
			const assistant = createCsChatMessage({
				role: 'assistant', content: delta, timestamp: new Date(), streaming: true, models: [...this.turnModels]
			});
			this.streamingId = assistant.id;
			this.messages = [...this.messages, assistant];
		} else {
			const previous = this.messages[index];
			this.messages = this.replaceAt(this.messages, index, {
				...previous, content: previous.content + delta, activity: null, models: [...this.turnModels]
			});
		}
		this.cdr.markForCheck();
	}

	private finishStreaming(): void {
		this.busy = false;
		this.clearWatchdog();
		if (this.streamingId) {
			const index = this.messages.findIndex(m => m.id === this.streamingId);
			if (index !== -1 && this.messages[index].streaming) {
				this.messages = this.replaceAt(this.messages, index, {
					...this.messages[index], streaming: false, activity: null, models: [...this.turnModels]
				});
			}
		}
		this.streamingId = null;
		this.cdr.markForCheck();
	}

	private replaceAt(list: CsChatMessage[], index: number, value: CsChatMessage): CsChatMessage[] {
		const copy = list.slice();
		copy[index] = value;
		return copy;
	}
}
